import { has, isString } from "lodash";

const getValue = (key) => (value) => {
  if (["PlannerAction", "RetailerAction"].includes(key)) {
    return Boolean(value);
  }
  return value;
};

export const getCountByKey = (pog, key, value = "") => {
  const pogList = pog?.subRows ?? [];
  let count = 0;
  let newValue = value;
  const valueMapper = getValue(key);
  for (let i = 0; i < pogList.length; i++) {
    if (
      valueMapper(newValue) &&
      valueMapper(pogList[i][key]) === valueMapper(newValue)
    ) {
      count = count + 1;
    }
  }
  return count;
};

const updateNotesAction = (entity, updatedRow) => {
  let updatedChange = {};
  if (
    has(updatedRow, "PlannerAction") &&
    entity?.PlannerAction !== updatedRow?.PlannerAction
  ) {
    updatedChange = {
      PlannerAction: updatedRow?.PlannerAction,
    };
  } else if (
    has(updatedRow, "RetailerAction") &&
    entity?.RetailerAction !== updatedRow?.RetailerAction
  ) {
    updatedChange = {
      RetailerAction: updatedRow?.RetailerAction,
    };
  }
  return updatedChange;
};

export const updateNotes = (entity, updatedRow) => {
  let changes = {};
  if (
    has(updatedRow, "plannernotesid") &&
    entity?.plannernotesid !== updatedRow?.plannernotesid
  ) {
    changes = {
      plannernotesid: updatedRow?.plannernotesid,
      PlannerAction: !isString(updatedRow?.plannernotesid)
        ? updatedRow?.PlannerAction
        : false,
    };
  } else if (
    has(updatedRow, "retailernotesid") &&
    entity?.retailernotesid !== updatedRow?.retailernotesid
  ) {
    changes = {
      retailernotesid: updatedRow?.retailernotesid,
      RetailerAction: !isString(updatedRow?.retailernotesid)
        ? updatedRow?.RetailerAction
        : false,
    };
  } else {
    changes = { ...updateNotesAction(entity, updatedRow) };
  }
  return changes;
};

// Auto update items if pog notes related any field changes.
export const getAutoUpdatedPogItems = (
  pog = {},
  updatedRow = {},
  selections = []
) => {
  const itemList = pog?.subRows ?? [];
  let changedItems = [];
  let updatedPogChange = updateNotes(pog, updatedRow);
  if (selections.length) {
    for (let x = 0; x < itemList.length; x++) {
      if (selections.includes(itemList[x].id)) {
        changedItems.push({
          ...itemList[x],
          ...updatedPogChange,
        });
      } else {
        changedItems.push(itemList[x]);
      }
    }
  } else {
    changedItems = [...itemList];
  }
  return [updatedPogChange, changedItems];
};

export const autoResetPogFields = (
  isItemChanged = false,
  updatedRow = {},
  pog = {},
  selections = []
) => {
  let changes = {};
  const selectionLength = selections?.length - 1;

  if (isItemChanged) {
    if (
      has(updatedRow, "plannernotesid") &&
      pog?.plannernotesid !== updatedRow.plannernotesid
    ) {
      const notesCount = getCountByKey(
        pog,
        "plannernotesid",
        updatedRow.plannernotesid
      );
      const value = notesCount >= selectionLength;
      const actionCount = value ? getCountByKey(pog, "PlannerAction", true) : 0;
      changes = {
        plannernotesid: value ? updatedRow.plannernotesid : null,
        PlannerAction: actionCount === selectionLength,
      };
    } else if (
      has(updatedRow, "retailernotesid") &&
      pog?.retailernotesid !== updatedRow.retailernotesid
    ) {
      const notesCount = getCountByKey(
        pog,
        "retailernotesid",
        updatedRow.retailernotesid
      );
      const value = notesCount >= selectionLength;
      const actionCount = value
        ? getCountByKey(pog, "RetailerAction", true)
        : 0;
      changes = {
        retailernotesid: value ? updatedRow.retailernotesid : null,
        RetailerAction: actionCount >= selectionLength,
      };
    } else if (
      has(updatedRow, "PlannerAction") &&
      pog?.PlannerAction !== updatedRow.PlannerAction
    ) {
      const selectionCount = getCountByKey(
        pog,
        "PlannerAction",
        updatedRow.PlannerAction
      );
      const value = selectionCount >= selectionLength;
      changes = {
        PlannerAction: value,
      };
    } else if (
      has(updatedRow, "RetailerAction") &&
      pog?.RetailerAction !== updatedRow.RetailerAction
    ) {
      const selectionCount = getCountByKey(
        pog,
        "RetailerAction",
        updatedRow.RetailerAction
      );
      const value = selectionCount >= selectionLength;
      changes = {
        RetailerAction: value,
      };
    } else {
      changes = {};
    }
  }
  return changes;
};

const updateMatchValue = (matches, currentItem, skipKey, key) => {
  if (!matches[skipKey]) {
    if (!matches[key]) {
      if (currentItem[key]) {
        matches[key] = currentItem[key];
      } else {
        matches[key] = null;
        matches[skipKey] = true;
      }
    } else {
      if (matches[key] !== currentItem[key]) {
        if (key === "retailernotesid") {
          matches.RetailerAction = null;
          matches.skipRetailerAction = true;
        } else if (key === "plannernotesid") {
          matches.PlannerAction = null;
          matches.skipPlannerAction = true;
        }
        matches[key] = null;
        matches[skipKey] = true;
      }
    }
  }
};

export const getPogPrefillValues = (selections = [], itemList = []) => {
  const matches = {
    multiplier: null,
    skipMultiplier: false,
    PlannerAction: null,
    skipPlannerAction: false,
    RetailerAction: null,
    skipRetailerAction: false,
    plannernotesid: null,
    skipplannernotesid: false,
    retailernotesid: null,
    skipretailernotesid: false,
  };
  for (let i = 0; i < selections.length; i++) {
    const selectedItemId = selections[i];
    const currentItem = itemList.find((item) => item.id === selectedItemId);
    if (currentItem) {
      // Check multiplier
      updateMatchValue(matches, currentItem, "skipMultiplier", "multiplier");

      // Check planner note id
      updateMatchValue(
        matches,
        currentItem,
        "skipplannernotesid",
        "plannernotesid"
      );

      // Check planner note action
      if (matches.plannernotesid) {
        updateMatchValue(
          matches,
          currentItem,
          "skipPlannerAction",
          "PlannerAction"
        );
      }

      // Check retailer note id
      updateMatchValue(
        matches,
        currentItem,
        "skipretailernotesid",
        "retailernotesid"
      );

      // Check retailer note action
      if (matches.retailernotesid) {
        updateMatchValue(
          matches,
          currentItem,
          "skipRetailerAction",
          "RetailerAction"
        );
      }
    }
  }
  return matches;
};
